// src/pages/ConfigureBot/ConfigureBot.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import { toast } from 'react-toastify';
import './ConfigureBot.css';

// ---------------------------------------------------------------
// Environment
// ---------------------------------------------------------------
const API_BASE = process.env.REACT_APP_API_BASE_URL;

const ConfigureBot = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [tabValue, setTabValue] = useState(0);

  // Initialize all 6 bots
  const [botSettings, setBotSettings] = useState({
    complianceBot: { enabled: true,  reportFrequency: 'daily',  expanded: true, lastRun: null },
    financialBot:  { enabled: false, reportFrequency: 'weekly', expanded: true, lastRun: null },
    auditBot:      { enabled: true,  reportFrequency: 'daily',  expanded: true, lastRun: null },
    documentBot:   { enabled: false, reportFrequency: 'daily',  expanded: true, lastRun: null },
    screeningBot:  { enabled: true,  reportFrequency: 'daily',  expanded: true, lastRun: null },
    pricingBot:    { enabled: true,  reportFrequency: 'weekly', expanded: true, lastRun: null },
    vesselBot:     { enabled: false, reportFrequency: 'daily',  expanded: true, lastRun: null },
    riskBot:       { enabled: true,  reportFrequency: 'daily',  expanded: true, lastRun: null },
  });

  const [tasks, setTasks] = useState([]);

  const [logs, setLogs] = useState([]);
  const [editTask, setEditTask] = useState(null);
  const [editTaskOpen, setEditTaskOpen] = useState(false);
  const [allEnabled, setAllEnabled] = useState(true);
  const [botHistory, setBotHistory] = useState([]);

  // -----------------------------------------------------------------
  // FETCH ALL BOT STATUS (GET /bot-status/)
  // -----------------------------------------------------------------
  const fetchAllBotStatus = async () => {
    try {
      const token = getAuthToken();
      console.log('Fetching all bot status from:', `${API_BASE}/bot-status/`);
      
      const res = await fetch(`${API_BASE}/bot-status/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Bot status response status:', res.status);

      if (res.status === 401) {
        localStorage.removeItem('authToken');
        throw new Error('Token expired');
      }
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Bot status error response:', errorText);
        throw new Error('Failed to fetch bot status');
      }

      const data = await res.json();
      console.log('Bot status data:', data);
      
      // Update tasks from bot-status response
      const tasksList = data.map(botData => ({
        id: botData.bot,
        bot: botData.bot,
        frequency: botData.frequency,
        nextRun: new Date(botData.next_run).toLocaleString(),
        status: botData.status,
      }));
      setTasks(tasksList);
      
      // Update botSettings based on response
      data.forEach(botData => {
        const botKey = botData.bot === 'Compliance Bot' ? 'complianceBot' : 
                       botData.bot === 'Document Bot' ? 'documentBot' : null;
        
        if (botKey) {
          setBotSettings(prev => ({
            ...prev,
            [botKey]: {
              ...prev[botKey],
              enabled: botData.status === 'Active',
              reportFrequency: botData.frequency.toLowerCase(),
              lastRun: botData.next_run,
              status: botData.status,
            },
          }));
        }
      });
      addLog('System', 'All bot statuses loaded');
    } catch (err) {
      console.error('fetchAllBotStatus error:', err);
      if (!err.message.includes('Token')) {
        toast.error(`Failed to fetch all bot status: ${err.message}`, { autoClose: 4000 });
      }
    }
  };

  // -----------------------------------------------------------------
  // FETCH BOT HISTORY (GET /bot-history/)
  // -----------------------------------------------------------------
  const fetchBotHistory = async (limit = 50, skip = 0) => {
    try {
      const token = getAuthToken();
      const url = `${API_BASE}/bot-history/?limit=${limit}&skip=${skip}`;
      console.log('Fetching bot history from:', url);
      
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Bot history response status:', res.status);

      if (res.status === 401) {
        localStorage.removeItem('authToken');
        throw new Error('Token expired');
      }
      if (!res.ok) {
        const errorText = await res.text();
        console.error('Bot history error response:', errorText);
        throw new Error('Failed to fetch bot history');
      }

      const data = await res.json();
      console.log('Bot history data:', data);
      
      // data is an array of history objects
      setBotHistory(data);
      
      // Convert history to logs format
      const historyLogs = data.map((item, index) => {
        // Remove TransactionSet ID, validation queue messages, and "Unusual Documents Check,"
        let cleanMessage = item.action;
        if (cleanMessage) {
          cleanMessage = cleanMessage
            .replace(/\(TransactionSet ID: \d+\)/g, '')
            .replace(/Queued \d+ validation\(s\):/g, '')
            .replace(/Unusual Documents Check,/g, '')
            .trim();
        }
        
        return {
          id: `history-${item.id || index}`,
          bot: item.bot === 'string' ? 'Unknown' : item.bot,
          timestamp: new Date(item.timestamp).toLocaleString(),
          message: cleanMessage,
        };
      });
      
      setLogs(prev => [...historyLogs, ...prev]);
      addLog('System', `Bot history loaded (${data.length} records)`);
    } catch (err) {
      console.error('fetchBotHistory error:', err);
      if (!err.message.includes('Token')) {
        toast.error(`Failed to fetch bot history: ${err.message}`, { autoClose: 4000 });
      }
    }
  };

  // -----------------------------------------------------------------
  // Auth
  // -----------------------------------------------------------------
  const getAuthToken = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('Please log in again.', { autoClose: 3000 });
      navigate('/login');
      throw new Error('No auth token');
    }
    return token;
  };

  // -----------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------
  const getDepartment = (bot) => {
    const map = {
      complianceBot: 'Compliance',
      financialBot: 'Financial',
      auditBot: 'Audit',
      documentBot: 'Documentation',
      screeningBot: 'Screening',
      pricingBot: 'Pricing',
      vesselBot: 'Vessel',
      riskBot: 'Risk',
    };
    return map[bot] || bot;
  };

  const addLog = (bot, message) => {
    setLogs(p => [
      ...p,
      {
        id: `log${p.length + 1}`,
        bot,
        timestamp: new Date().toLocaleString(),
        message,
      },
    ]);
  };

  // -----------------------------------------------------------------
  // FETCH BOT STATUS (GET) - Only compliance & document hit API
  // -----------------------------------------------------------------
  const fetchBot = async (bot) => {
    if (!['complianceBot', 'documentBot'].includes(bot)) return; // Only these hit backend
    const endpoint = bot === 'complianceBot' ? 'compliance-bot' : 'document-bot';
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/${endpoint}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        localStorage.removeItem('authToken');
        throw new Error('Token expired');
      }
      if (!res.ok) throw new Error('Failed to fetch');

      const data = await res.json();
      setBotSettings(prev => ({
        ...prev,
        [bot]: {
          ...prev[bot],
          enabled: !data.active, // SWAPPED: Backend sends inverted value
          reportFrequency: data.frequency.toLowerCase(),
          lastRun: data.last_run,
        },
      }));
      addLog(bot, 'Config loaded');
    } catch (err) {
      console.error(err);
      if (!err.message.includes('Token')) {
        toast.error(`API Error: ${err.message}`, { autoClose: 4000 });
      }
    }
  };

  // -----------------------------------------------------------------
  // SAVE BOT STATUS (PUT) - Only compliance & document
  // -----------------------------------------------------------------
  const saveBot = async (bot, overrideFrequency = null) => {
    if (!['complianceBot', 'documentBot'].includes(bot)) {
      addLog(bot, `Updated: ${botSettings[bot].reportFrequency}, active: ${botSettings[bot].enabled}`);
      return;
    }

    const cfg = botSettings[bot];
    const endpoint = bot === 'complianceBot' ? 'compliance-bot' : 'document-bot';
    
    // Use override frequency if provided, otherwise use current state
    const frequencyToSend = overrideFrequency || cfg.reportFrequency;
    
    const payload = {
      active: !cfg.enabled, // SWAPPED: Send inverted value to backend
      schedule_description: bot === 'complianceBot' ? 'Automated sanctions screening' : 'Automated document processing',
      frequency: frequencyToSend,
    };

    console.log('Sending payload:', payload); // Debug log

    try {
      setSaving(prev => ({ ...prev, [bot]: true }));
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/${endpoint}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        localStorage.removeItem('authToken');
        throw new Error('Authentication failed');
      }
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail?.[0]?.msg || 'Validation failed');
      }

      const data = await res.json();
      setBotSettings(prev => ({
        ...prev,
        [bot]: { ...prev[bot], lastRun: data.last_run },
      }));
      
      // Refresh bot status after saving
      await fetchAllBotStatus();
      
      addLog(bot, `Updated: ${frequencyToSend}, active: ${cfg.enabled}`);
      toast.success('Bot settings saved', { autoClose: 1500 });
    } catch (err) {
      console.error(err);
      if (!err.message.includes('Authentication')) {
        toast.error(`Save Failed: ${err.message}`, { autoClose: 4000 });
      }
    } finally {
      setSaving(prev => ({ ...prev, [bot]: false }));
    }
  };

  // -----------------------------------------------------------------
  // Load on Mount
  // -----------------------------------------------------------------
  useEffect(() => {
    const loadData = async () => {
      if (!process.env.REACT_APP_API_BASE_URL) {
        console.warn('API_BASE_URL missing in .env');
      }
      
      try {
        await Promise.all([
          fetchAllBotStatus(), // This now loads bot status AND tasks
          fetchBotHistory(),
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // -----------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------
  const handleTabChange = (_, v) => setTabValue(v);

  const handleToggle = (bot, setting) => {
    setBotSettings(p => ({ ...p, [bot]: { ...p[bot], [setting]: !p[bot][setting] } }));
    if (setting === 'enabled' && ['complianceBot', 'documentBot'].includes(bot)) {
      saveBot(bot);
    } else {
      addLog(bot, `${setting} toggled`);
    }
  };

  const handleFrequency = (bot, value) => {
    setBotSettings(p => ({ ...p, [bot]: { ...p[bot], reportFrequency: value } }));
    if (['complianceBot', 'documentBot'].includes(bot)) {
      // Pass the new frequency value directly to saveBot
      setTimeout(() => saveBot(bot, value), 100);
    } else {
      addLog(bot, `Frequency → ${value}`);
    }
  };

  const handleExpand = bot => {
    setBotSettings(p => ({ ...p, [bot]: { ...p[bot], expanded: !p[bot].expanded } }));
  };

  const handleToggleAll = () => {
    const newEnabled = !allEnabled;
    setAllEnabled(newEnabled);
    const updated = {};
    Object.keys(botSettings).forEach(b => {
      updated[b] = { ...botSettings[b], enabled: newEnabled };
    });
    setBotSettings(updated);
    ['complianceBot', 'documentBot'].forEach(saveBot);
    addLog('All', `All bots ${newEnabled ? 'enabled' : 'disabled'}`);
  };

  const handleEditTask = task => { 
    setEditTask({ ...task }); 
    setEditTaskOpen(true); 
  };

  const handleDeleteTask = id => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;
    setTasks(p => p.filter(t => t.id !== id));
    addLog(task.bot, `Task deleted`);
    toast.success('Task Deleted', { autoClose: 1500 });
  };

  const handleSaveTask = () => {
    if (!editTask?.frequency) {
      toast.error('Frequency required', { autoClose: 3000 });
      return;
    }
    setTasks(p => p.map(t => (t.id === editTask.id ? editTask : t)));
    addLog(editTask.bot, `Task updated`);
    setEditTaskOpen(false);
    setEditTask(null);
    toast.success('Task Updated', { autoClose: 1500 });
  };

  // -----------------------------------------------------------------
  // Loading
  // -----------------------------------------------------------------
  if (loading) {
    return (
      <Box className="cb-container" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress className="cb-loading" />
      </Box>
    );
  }

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  return (
    <Box className="cb-container">
      <div className="cb-particles">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="cb-particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      <Box className="cb-content">
        <Box className="cb-module-tabs">
          <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="Bot Settings" />
            <Tab label="Tasks" />
            <Tab label="Activity Logs" />
          </Tabs>
        </Box>

        <Box className="cb-validations-section">
          {/* === Bot Settings === */}
          {tabValue === 0 && (
            <Box className="cb-layout">
              <Box className="cb-controls">
                <Button variant="contained" onClick={handleToggleAll} className="cb-button">
                  {allEnabled ? 'Disable All Bots' : 'Enable All Bots'}
                </Button>
              </Box>

              {/* 2x3 Grid Layout */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                  gap: 2,
                  mt: 3,
                }}
              >
                {Object.keys(botSettings).map(bot => {
                  const isLocked = !['complianceBot', 'documentBot'].includes(bot);
                  
                  return (
                    <Card 
                      className="cb-card" 
                      key={bot} 
                      sx={{ 
                        minWidth: 280,
                        position: 'relative',
                        opacity: isLocked ? 0.6 : 1,
                        filter: isLocked ? 'blur(2px)' : 'none',
                        pointerEvents: isLocked ? 'none' : 'auto',
                      }}
                    >
                      {isLocked && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            pointerEvents: 'none',
                          }}
                        >
                          <LockIcon 
                            sx={{ 
                              fontSize: '80px',
                              color: 'rgba(255, 255, 255, 0.9)',
                              filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.5))',
                            }} 
                          />
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                        <Typography variant="h6" className="cb-title">
                          {bot === 'complianceBot' ? 'Compliance Bot' :
                           bot === 'financialBot'   ? 'Financial Bot' :
                           bot === 'auditBot'       ? 'Audit Bot' :
                           bot === 'documentBot'    ? 'Document Bot' :
                           bot === 'screeningBot'   ? 'Screening Bot' :
                           bot === 'pricingBot'     ? 'Pricing Bot' :
                           bot === 'vesselBot'      ? 'Vessel Bot' :
                           'Risk Bot'}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        <Tooltip title={botSettings[bot].expanded ? 'Collapse' : 'Expand'}>
                          <IconButton onClick={() => handleExpand(bot)}>
                            <ExpandMoreIcon
                              sx={{
                                transform: botSettings[bot].expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform .2s',
                              }}
                              className="cb-icon"
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Collapse in={botSettings[bot].expanded}>
                        <CardContent>
                          <Box className="cb-status">
                            <Chip
                              label={botSettings[bot].enabled ? 'Running' : 'Idle'}
                              color={botSettings[bot].enabled ? 'success' : 'default'}
                            />
                            {botSettings[bot].lastRun && (
                              <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                Last: {new Date(botSettings[bot].lastRun).toLocaleString()}
                              </Typography>
                            )}
                          </Box>

                          <Box className="cb-setting">
                            <Tooltip title="Enable or disable this bot">
                              <Typography>Enable Bot</Typography>
                            </Tooltip>
                            <Switch
                              checked={botSettings[bot].enabled}
                              onChange={() => handleToggle(bot, 'enabled')}
                              disabled={saving[bot]}
                            />
                          </Box>

                          <Box className="cb-setting">
                            <FormControl fullWidth>
                              <Select
                                value={botSettings[bot].reportFrequency}
                                onChange={e => handleFrequency(bot, e.target.value)}
                                className="cb-select"
                                disabled={saving[bot]}
                              >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>

                          {/* REMOVED: Configure Task Button */}
                        </CardContent>
                      </Collapse>
                    </Card>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* === Tasks === */}
          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" className="cb-title">Configured Tasks</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table className="cb-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bot</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>Next Run</TableCell>
                      <TableCell>Status</TableCell>
                      {/* REMOVED: Actions Column */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tasks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No tasks configured yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tasks.map(t => (
                        <TableRow key={t.id}>
                          <TableCell>{t.bot}</TableCell>
                          <TableCell>{t.frequency}</TableCell>
                          <TableCell>{t.nextRun}</TableCell>
                          <TableCell>
                            <Chip label={t.status} color={t.status === 'Active' ? 'success' : 'default'} />
                          </TableCell>
                          {/* REMOVED: Action Buttons */}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {/* === Activity Logs === */}
          {tabValue === 2 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" className="cb-title">Recent Activity Logs</Typography>
                <Button 
                  variant="outlined" 
                  onClick={() => fetchBotHistory(50, 0)}
                  className="cb-btn-outlined"
                  size="small"
                >
                  Refresh History
                </Button>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Table className="cb-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bot</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Timestamp</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No activity logs yet
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map(log => (
                        <TableRow key={log.id}>
                          <TableCell>{log.bot}</TableCell>
                          <TableCell>{log.message}</TableCell>
                          <TableCell>{log.timestamp}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {/* === Edit Task Dialog === */}
          <Dialog open={editTaskOpen} onClose={() => setEditTaskOpen(false)} classes={{ paper: 'cb-dialog' }}>
            <DialogTitle className="cb-dialog-title">Edit Task</DialogTitle>
            <DialogContent className="cb-dialog-content">
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={editTask?.frequency || ''}
                  onChange={e => setEditTask({ ...editTask, frequency: e.target.value })}
                  className="cb-select"
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Next Run"
                value={editTask?.nextRun || ''}
                onChange={e => setEditTask({ ...editTask, nextRun: e.target.value })}
                fullWidth
                className="cb-textfield"
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions className="cb-dialog-actions">
              <Button onClick={() => setEditTaskOpen(false)} className="cb-button">Cancel</Button>
              <Button onClick={handleSaveTask} className="cb-button">Save</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default ConfigureBot;